import {DocumentTypeVal, PersonLegalRole} from '@modifi/enums'
import {aGenericError, AnyAction} from '@modifi/redux'
import {aPush} from '@modifi/router'
import {createSelector} from 'reselect'
import {
  filterDocumentsByInputFieldName,
  LEGAL_REPRESENTATIVE_COUNTRY,
  LEGAL_REPRESENTATIVE_NATIONALITY,
  LEGAL_REPRESENTATIVE_POLITICALLY_EXPOSED,
  LEGAL_REPRESENTATIVE_POLITICALLY_EXPOSED_REASON,
  LEGAL_REPRESENTATIVE_IS_SIGNATORY,
  aUserFeedBackSubmitSuccessful,
} from '@modifi/plugin-onboarding'
import {
  LegalRepresentativeView,
  PatchPersonIdentificationRequestView,
  PatchPersonRequestView,
} from '@modifi/onboarding-api-clients'
import {
  LEGAL_REPRESENTATIVES_DOCUMENT_INPUT_FIELD_NAMES,
  LEGAL_REPRESENTATIVES_ROLE_INPUT_FIELD_NAMES,
} from '../constants'
import {getLogger} from '../../../../utils'
import {
  aFetchDocumentsByExternalPersonId,
  aUpdatePersonDocuments,
  selectDocumentsByExternalPersonId,
} from '../../../../data/person-documents'
import {aCreatePerson, aUpdatePerson} from '../../../../modules/persons'
import {
  mapLegalRepresentativeToFormData,
  mapLegalRepresentativeToRequest,
  pickLegalRepresentativeDocumentProperties,
  pickLegalRepresentativePersonProperties,
  pickLegalRepresentativeRoleProperties,
} from '../utils'
import {aClearDataSourcePerson, selectDataSourcePerson} from './data-source'
import {aLinkPersonToLegalRole, aUpdatePersonToLegalRoleLink} from '../../../../modules/legal-roles'
import {ROUTE_COMPANY_PROFILE_LEGAL_REPRESENTATIVES} from '../../../../routes/constants'
import {selectInputFieldNamesBySectionName} from '../../../../modules/input-fields'
import {SECTION_LEGAL_REPRESENTATIVES} from '../../../../constants/sections'
import {AppThunkAction} from '../../../../types/redux'
import {rootSelector} from '../../../../redux/root-selector'
import {
  aAppendLegalRepresentativeToCompany,
  aFetchLegalRepresentatives,
  aUpdateLocalLegalRepresentative,
  selectLegalRepresentativeByExternalId,
  selectLegalRepresentativesWhereLoaded,
} from '../../../../data/legal-representatives'
import {aFetchCompany, selectCurrentCompaniesCountryCode} from '../../../../data/company'
import {
  aFetchBeneficialOwners,
  selectBeneficialOwnerByExternalId,
} from '../../../../data/beneficial-owners'
import {aFetchInputFields} from '../../../../data/input-fields'
import {aFetchDirectors, selectDirectorByExternalId} from '../../../../data/directors'

const logger = getLogger('legal-representatives-page')

const ACTION_SAVE_PENDING = 'pages/legal-representatives/SAVE_PENDING'
const ACTION_SAVE_SUCCESS = 'pages/legal-representatives/SAVE_SUCCESS'
const ACTION_SAVE_ERROR = 'pages/legal-representatives/SAVE_ERROR'
const ACTION_SAVE_DISCARD = 'pages/legal-representatives/SAVE_DISCARD'

type DocumentChange = {
  added: {fileId: string; documentType: DocumentTypeVal}[]
  removed: string[]
}

type FormSubmitState = {
  error: null | {message: string; code: number; fieldErrors: any}
  requestPending: boolean
}

const initialState: FormSubmitState = {
  requestPending: false,
  error: null,
}

export default (state = initialState, {type, payload}: AnyAction): FormSubmitState => {
  switch (type) {
    case ACTION_SAVE_PENDING:
      return {
        ...state,
        requestPending: true,
        error: null,
      }
    case ACTION_SAVE_SUCCESS:
      return {
        ...state,
        requestPending: false,
      }
    case ACTION_SAVE_ERROR: {
      const {code, fieldErrors, message} = payload
      return {
        ...state,
        requestPending: false,
        error: {
          code,
          message,
          fieldErrors,
        },
      }
    }
    case ACTION_SAVE_DISCARD:
      return {...initialState}
    default:
      return state
  }
}

const baseSelector = createSelector(rootSelector, root => root?.pages?.legalRepresentatives?.form)
export const selectSavePending = createSelector(baseSelector, subState => subState?.requestPending)
const selectSaveErrorObject = createSelector(baseSelector, subState => subState?.error)
export const selectSaveHasError = createSelector(selectSaveErrorObject, error => !!error)
export const selectSaveErrorMessage = createSelector(selectSaveErrorObject, error => error?.message)

export const selectInitializePagePending = (
  state: Record<string, any>,
  externalPersonId: string
): boolean => {
  if (externalPersonId) {
    const legalRepresentativesWhereLoaded = selectLegalRepresentativesWhereLoaded(state)
    if (!legalRepresentativesWhereLoaded) {
      return true
    }
  }
  return false
}

export const selectInitialFormData = (
  state: Record<string, any>,
  externalPersonId: string
): {defaultValues: Record<string, any>; initialValues: Record<string, any>} => {
  const inputFieldsToRender = selectInputFieldNamesBySectionName(
    state,
    SECTION_LEGAL_REPRESENTATIVES
  )

  const defaultValues: Record<string, any> = {
    [LEGAL_REPRESENTATIVE_POLITICALLY_EXPOSED_REASON]: '',
    [LEGAL_REPRESENTATIVE_POLITICALLY_EXPOSED]: false,
    [LEGAL_REPRESENTATIVE_IS_SIGNATORY]: true,
  }
  const initialValues: Record<string, any> = {
    'dataSource.externalPersonId': '',
    'dataSource.showSelect': false,
  }
  Object.assign(
    initialValues,
    LEGAL_REPRESENTATIVES_DOCUMENT_INPUT_FIELD_NAMES.reduce((result, inputName) => {
      // eslint-disable-next-line no-param-reassign
      result[inputName] = []
      return result
    }, {} as Record<string, []>)
  )

  const dataSourceExternalPersonId = selectDataSourcePerson(state)

  if (externalPersonId) {
    const documents =
      selectDocumentsByExternalPersonId(
        state,
        dataSourceExternalPersonId || externalPersonId
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      )?.map(({fileId, documentType}) => ({fileId, documentType: documentType!})) || []

    Object.assign(
      initialValues,
      LEGAL_REPRESENTATIVES_DOCUMENT_INPUT_FIELD_NAMES.reduce((result, inputName) => {
        // eslint-disable-next-line no-param-reassign
        result[inputName] = filterDocumentsByInputFieldName(inputName, documents)
        return result
      }, {} as Record<string, any>)
    )

    // externalPersonId is a legal rep
    const {address, ...rest} = selectLegalRepresentativeByExternalId(state, externalPersonId) || {}
    Object.assign(
      initialValues,
      mapLegalRepresentativeToFormData({
        ...address,
        ...rest,
      })
    )
  } else if (dataSourceExternalPersonId) {
    const documents =
      selectDocumentsByExternalPersonId(
        state,
        dataSourceExternalPersonId || externalPersonId
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      )?.map(({fileId, documentType}) => ({fileId, documentType: documentType!})) || []

    Object.assign(
      initialValues,
      LEGAL_REPRESENTATIVES_DOCUMENT_INPUT_FIELD_NAMES.reduce((result, inputName) => {
        // eslint-disable-next-line no-param-reassign
        result[inputName] = filterDocumentsByInputFieldName(inputName, documents)
        return result
      }, {} as Record<string, any>)
    )

    const {
      address,
      externalId: _,
      ...rest
    } = selectDirectorByExternalId(state, dataSourceExternalPersonId) ||
    selectBeneficialOwnerByExternalId(state, dataSourceExternalPersonId) ||
    {}

    Object.assign(
      initialValues,
      mapLegalRepresentativeToFormData({
        ...address,
        ...rest,
      })
    )
  } else {
    if (inputFieldsToRender.includes(LEGAL_REPRESENTATIVE_NATIONALITY)) {
      defaultValues[LEGAL_REPRESENTATIVE_NATIONALITY] = selectCurrentCompaniesCountryCode(state)
    }
    defaultValues[LEGAL_REPRESENTATIVE_COUNTRY] = selectCurrentCompaniesCountryCode(state)
  }

  return {
    initialValues,
    defaultValues,
  }
}

export const aInitializePage =
  (externalCompanyId: string, externalPersonId?: string): AppThunkAction<Promise<void>> =>
  async (dispatch): Promise<void> => {
    // always there when company was loaded
    // dispatch(aEnsureLegalRepresentatives())
    if (externalPersonId) {
      dispatch(aFetchDocumentsByExternalPersonId(externalPersonId))
    }
  }

export const aReset =
  (): AppThunkAction<void> =>
  (dispatch): void => {
    dispatch({
      type: ACTION_SAVE_DISCARD,
    })
    dispatch(aClearDataSourcePerson())
  }

export const aSavePage =
  (
    changes: Record<string, any>,
    externalCompanyId: string,
    externalPersonId?: string
  ): AppThunkAction<Promise<void>> =>
  async (dispatch, getState): Promise<void> => {
    logger.info('Submitting legal representative page')
    logger.debug('changes: %o', changes)

    const documentChanges = Object.values(pickLegalRepresentativeDocumentProperties(changes))
    const personChangesAsRequestData = mapLegalRepresentativeToRequest(
      pickLegalRepresentativePersonProperties(changes)
    ) as PatchPersonRequestView
    const roleChangesAsRequestData = mapLegalRepresentativeToRequest(
      pickLegalRepresentativeRoleProperties(changes)
    ) as PatchPersonIdentificationRequestView

    let updatedPerson

    dispatch({type: ACTION_SAVE_PENDING})

    const createdLegalRepresentative: LegalRepresentativeView = {
      externalId: externalPersonId,
    }
    if (!externalPersonId && typeof changes['dataSource.externalPersonId'] === 'string') {
      // eslint-disable-next-line no-param-reassign
      externalPersonId = changes['dataSource.externalPersonId']
      Object.assign(
        createdLegalRepresentative,
        selectDirectorByExternalId(getState(), externalPersonId) ||
          selectBeneficialOwnerByExternalId(getState(), externalPersonId)
      )

      // adding legal role changes when creating legal rep from existing person since BE
      // updates legal role fields for what it recieves and doesn't automatically add based upon
      // existing person id
      LEGAL_REPRESENTATIVES_ROLE_INPUT_FIELD_NAMES.forEach((legalRepRoleInputField: string) => {
        const roleInputField = legalRepRoleInputField.replace('legalRepresentative.', '')
        // @ts-expect-error
        if (roleChangesAsRequestData[roleInputField] === undefined) {
          // @ts-expect-error
          roleChangesAsRequestData[roleInputField] = createdLegalRepresentative[roleInputField]
        }
      })
    }

    const updateLegalRole =
      !!externalPersonId && !!selectLegalRepresentativeByExternalId(getState(), externalPersonId)

    let existingPersonUpdated = false
    try {
      logger.info('Create or update the person: %o', personChangesAsRequestData)
      if (externalPersonId) {
        if (Object.keys(personChangesAsRequestData).length > 0) {
          existingPersonUpdated = true
          logger.debug('Person already exists - externalId is: %s, updating...', externalPersonId)
          updatedPerson = await dispatch(
            aUpdatePerson(externalPersonId, personChangesAsRequestData) as any
          )
          Object.assign(createdLegalRepresentative, updatedPerson)
        } else {
          logger.debug('No person related changes detected')
        }
      } else {
        logger.debug('Person does not exist yet, creating...')
        updatedPerson = await dispatch(aCreatePerson(personChangesAsRequestData) as any)
        Object.assign(createdLegalRepresentative, updatedPerson)
        // eslint-disable-next-line no-param-reassign
        externalPersonId = updatedPerson.externalId
      }

      logger.info(
        'Creating or updating the person legal representative link: %o',
        roleChangesAsRequestData
      )

      if (!externalPersonId) {
        throw new Error(`Unknown externalPersonId`)
      }

      if (updateLegalRole) {
        if (Object.keys(roleChangesAsRequestData).length > 0) {
          logger.debug('Person is already a legal representative, updating...')

          Object.assign(createdLegalRepresentative, roleChangesAsRequestData)
          // @todo handle address?
          if (createdLegalRepresentative.externalId) {
            dispatch(
              aUpdateLocalLegalRepresentative(
                createdLegalRepresentative as LegalRepresentativeView & {
                  externalId: NonNullable<LegalRepresentativeView['externalId']>
                },
                true
              )
            )
          }

          await dispatch(
            aUpdatePersonToLegalRoleLink(
              externalPersonId,
              PersonLegalRole.LEGAL_REPRESENTATIVE,
              roleChangesAsRequestData
            ) as unknown as AnyAction
          )
        } else {
          logger.debug('No role related changes detected')
        }
      } else {
        logger.debug('Person is not yet a legal representative, creating...')

        Object.assign(createdLegalRepresentative, roleChangesAsRequestData)
        await dispatch(
          aLinkPersonToLegalRole(
            externalPersonId,
            PersonLegalRole.LEGAL_REPRESENTATIVE,
            roleChangesAsRequestData
          )
        )

        if (createdLegalRepresentative.externalId) {
          await dispatch(
            aAppendLegalRepresentativeToCompany(
              createdLegalRepresentative as LegalRepresentativeView & {
                externalId: NonNullable<LegalRepresentativeView['externalId']>
              }
            )
          )
        }
      }

      logger.info('Linking & unlinking documents to the person: %o', documentChanges)
      if (documentChanges.length > 0) {
        const mergedChanges = documentChanges.reduce(
          (result, change: DocumentChange) => {
            if (change.added && change.added.length > 0) {
              result.added.push(...change.added)
            }
            if (change.removed && change.removed.length > 0) {
              result.removed.push(...change.removed)
            }
            return result
          },
          {added: [], removed: []} as DocumentChange
        )
        await dispatch(aUpdatePersonDocuments(externalPersonId, mergedChanges))
      } else {
        logger.debug('No document related changed detected')
      }
    } catch (error: any) {
      logger.error('Error while creating or updating a person: %o', error)
      dispatch(aGenericError(ACTION_SAVE_ERROR, error))
      return
    }

    await Promise.all([
      dispatch(aFetchInputFields()),
      dispatch(aFetchCompany()),
      dispatch(aFetchLegalRepresentatives()),
    ])

    // if Exisiting person updated is also a director or UBO then fetch directors
    // or UBOs to update the their tables respectively in UI
    if (existingPersonUpdated && !!selectDirectorByExternalId(getState(), externalPersonId)) {
      await dispatch(aFetchDirectors())
    }
    if (
      existingPersonUpdated &&
      !!selectBeneficialOwnerByExternalId(getState(), externalPersonId)
    ) {
      await dispatch(aFetchBeneficialOwners())
    }
    // dispatch(aFetchPersonDocuments(externalPersonId))

    // redirect to newly created ultimate legal representative
    dispatch(aPush(ROUTE_COMPANY_PROFILE_LEGAL_REPRESENTATIVES))

    dispatch({type: ACTION_SAVE_SUCCESS})
    dispatch(aUserFeedBackSubmitSuccessful(SECTION_LEGAL_REPRESENTATIVES))
  }
